import { takeEvery, all, select, put } from 'redux-saga/effects';
import { selectedProformaInputsContainerSelector } from 'store/selectors';
import {
  saveUW,
  savePostAcqUW,
  setSelectedValuation,
  setTransientValuation,
  appNotify,
  setIsSavingUW,
  setChannelWorkflowLoadingOverlayState,
  setSelectedProformaInputsContainer,
  setErrorListModalState,
  afterSaveUWNavigate,
  fetchValuationHistory,
  setSelectedOpportunityValuationHistory
} from 'store/actions';
import { postWithTokenV2 } from 'api/network';
import { sendClientLog } from 'services/logService';
import { Constants } from 'utilities/constants';

export function* saveUWSaga() {
  yield takeEvery(saveUW, function* ({ payload }) {
    const { defaultValue, txnOppId, proformaInputsContainerId, transientValuation, history, nextTaskId, afterNavigation, callback } = payload;
    yield put(setIsSavingUW(true));
    yield put(setChannelWorkflowLoadingOverlayState({ isLoading: true, message: 'Saving...' }));

    const requestPayload = getPayload(transientValuation, defaultValue)

    try {
      const persistedValuation = yield postWithTokenV2(`/api/underwrite/prop/${txnOppId}/proformainputs/${proformaInputsContainerId}/valuation`, requestPayload);

      const [selectedProformaInputsContainer] = yield all([
        select(selectedProformaInputsContainerSelector)
      ]);

      const newValuations = selectedProformaInputsContainer.valuations.map(val => {
        return val.user.id === persistedValuation.user.id ? persistedValuation : val;
      });

      yield all([
        put(setSelectedProformaInputsContainer({
          ...selectedProformaInputsContainer,
          valuations: newValuations
        })),
        put(setSelectedValuation(persistedValuation)),
        put(setTransientValuation(persistedValuation)),
      ]);

      yield put(appNotify('Saved Underwrite', 'success', { autoHideDuration: 1500 }));

      yield put(setIsSavingUW(false));
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));

      if (nextTaskId) {
        history.push(`${Constants.Routes.UNDERWRITE_PROPERTY}/${nextTaskId}`);
      }
      
      if (afterNavigation) {
        yield put(afterSaveUWNavigate({ history, ...afterNavigation }))
      }

      if (nextTaskId == null && !afterNavigation) {
        function* valuationHistoryCallback(valuationHistory) {
          yield put(setSelectedOpportunityValuationHistory(valuationHistory));
        }
        yield put(fetchValuationHistory(txnOppId, valuationHistoryCallback))
      }

      if (callback) {
        callback();
      }

      sendClientLog('Info', `Frontend recieved saved valuation with id: ${persistedValuation?.id}`);
    } catch (error) {

      const { details } = error

      if (details.length === 1) {
        const [firstDetails] = details
        yield put(appNotify(firstDetails, 'error'));
      } else {
        yield put(setErrorListModalState({ header: 'Unable to save valuation for the following reasons', details }))
        const options = { showDetailsButton: true }
        yield put(appNotify('Unable to save valuation', 'error', options));
      }

      yield put(setIsSavingUW(false));
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));
    }
  });

  yield takeEvery(savePostAcqUW, function* ({ payload }) {
    const { defaultValue, proformaInputsContainerId, transientValuation, history, nextTaskId, afterNavigation } = payload;
    yield put(setIsSavingUW(true));
    yield put(setChannelWorkflowLoadingOverlayState({ isLoading: true, message: 'Saving...' }));

    try {
      const { id, type, notes, valuationInput, valuationsComps } = transientValuation;
      const { rent, rentType, afterRepairValue, afterRepairValueType } = valuationInput;
      const req = {
        id,
        valuationInput: {
          rent,
          rentType,
          afterRepairValue,
          afterRepairValueType
        },
        notes,
        type: type ?? defaultValue,
        valuationsComps
      };

      const persistedValuationRes = yield postWithTokenV2(`/api/valuationPostAcquisitions/${proformaInputsContainerId}`, req);

      const [selectedProformaInputsContainer] = yield all([
        select(selectedProformaInputsContainerSelector)
      ]);

      let updatedValuation = null;
      const newValuations = selectedProformaInputsContainer.valuations.map(val => {
        if (val.user.id === persistedValuationRes.userId) {
          updatedValuation = {
            ...val,
            ...req,
            id: persistedValuationRes.id,
            createdOn: persistedValuationRes.createdOn
          };
          return updatedValuation;
        }
        return val;
      });

      yield all([
        put(setSelectedProformaInputsContainer({
          ...selectedProformaInputsContainer,
          valuations: newValuations
        })),
        put(setSelectedValuation(updatedValuation)),
        put(setTransientValuation(updatedValuation)),
      ]);

      yield put(appNotify('Saved Underwrite', 'success', { autoHideDuration: 1500 }));

      yield put(setIsSavingUW(false));
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));

      if (nextTaskId) {
        history.push(`${Constants.Routes.UNDERWRITE_PROPERTY}/${nextTaskId}`);
      }

      if (afterNavigation) {
        yield put(afterSaveUWNavigate({ history, ...afterNavigation }))
      }

      sendClientLog('Info', `Frontend recieved saved post-acq valuation with id: ${updatedValuation?.id}`);
    } catch (error) {
      const { details } = error
      if (!details) {
        console.log(error)
        yield put(appNotify(error.message, 'error'));
        yield put(setIsSavingUW(false));
        yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));
        return;
      }

      if (details.length === 1) {
        const [firstDetails] = details
        yield put(appNotify(firstDetails, 'error'));
      } else {
        yield put(setErrorListModalState({ header: 'Unable to save valuation for the following reasons', details }))
        const options = { showDetailsButton: true }
        yield put(appNotify('Unable to save valuation', 'error', options));
      }

      yield put(setIsSavingUW(false));
      yield put(setChannelWorkflowLoadingOverlayState({ isLoading: false, message: null }));
    }
  });
}

const getPayload = (transientValuation, defaultValue) => {
  if (!defaultValue) {
    return transientValuation
  }

  const updatedTransientValuation = { ...transientValuation, type: defaultValue }
  return updatedTransientValuation
}